import { Injectable, isDevMode } from "@angular/core";
import { Action, Selector, State, StateContext, StateToken, Store } from "@ngxs/store";
import { FinOpsAwsServicesService } from "./finops-aws-services.service";
import {
    LoadFinOpsAwsServicesHistoricalData,
    LoadFinOpsAwsServicesChartsData,
    LoadDataTableAllAccountsFinOpsAwsServices,
    LoadFinOpsAwsServicesAllAccountsChartsFilteredData,
} from "./finops-aws-services.actions";
import { finalize, forkJoin } from "rxjs";
import { FinOpsAwsServicesStateModel } from "./finops-aws-services.model";
import { PreferencesFiltersState } from "src/app/store/preferences/filters/preferences-filters.state";
import { PreferencesFinopsState } from "src/app/store/preferences/finops/preferences-finops.state";
import { PreferencesChartsState } from "src/app/store/preferences/charts/preferences-charts.state";
import { TranslateService } from "@ngx-translate/core";
import { DateService, HistoricalDate } from "src/app/shared/service/date.service";
import { ItemTypeEnum } from "src/app/shared/enum/item-type.enum";
import { FloorPipe } from "src/app/shared/pipe/floor.pipe";
import { FormatNumberPipe } from "src/app/shared/pipe/format-number.pipe";
import { MetaRequestHelper } from "src/app/store/meta/meta-request.helper";
import { MetaService } from "src/app/store/meta/meta.service";
import { DataForDatatableCompute, FinOpsAwsServicesHelper } from "./finops-aws-services.helpers";
import { DateGranularityEnum } from "src/app/shared/enum/date-granularity.enum";
import { PreferencesProvidersState } from "src/app/store/preferences/providers/preferences-providers.state";
import { AwsConfig } from "src/app/store/preferences/providers/preferences-providers.model";
import { FinOpsContextState } from "src/app/store/finops/context/finops-context.state";
import { SelectFinOpsDate } from "src/app/store/finops/context/finops-context.actions";

const FINOPS_AWS_SERVICES_TOKEN: StateToken<FinOpsAwsServicesStateModel> = new StateToken('finOpsAwsServices');

const FinOpsServicesStateModelDefaults: FinOpsAwsServicesStateModel = {
    historicalDataByAccountByGranularityByDate: {
    },
    datatable: [],
    chartsDataByAccountByDate: {},
    chartsDataDisplayed: {
        filteredHistoricalChartData: {
            minCostRatioString: "",
            minCostValueString: "",
            prediction: false,
            costs: []
        },
        filteredCompareChartData: {
            minCostRatioString: "",
            minCostValueString: "",
            dateString: "",
            prediction: false,
            costs: []
        }
    },
    filteredCounters: {}
}

@State<FinOpsAwsServicesStateModel>({
    name: FINOPS_AWS_SERVICES_TOKEN,
    defaults: FinOpsServicesStateModelDefaults
})

@Injectable()
export class FinOpsAwsServicesState {

    private readonly KEY = FINOPS_AWS_SERVICES_TOKEN.getName()

    constructor(
        private finOpsAwsServicesService: FinOpsAwsServicesService,
        private store: Store,
        private floorPipe: FloorPipe,
        private numberFormatPipe: FormatNumberPipe,
        private dateService: DateService,
        private translate: TranslateService,
        private metaService: MetaService
    ) {
    }

    @Selector([FINOPS_AWS_SERVICES_TOKEN])
    static getDatatable(state: FinOpsAwsServicesStateModel) {
        return state.datatable
    }

    @Selector([FINOPS_AWS_SERVICES_TOKEN])
    static getFilteredCounters(state: FinOpsAwsServicesStateModel) {
        return state.filteredCounters
    }

    @Selector([FINOPS_AWS_SERVICES_TOKEN])
    static getAllChartsData(state: FinOpsAwsServicesStateModel) {
        return state.chartsDataByAccountByDate
    }

    @Selector([FINOPS_AWS_SERVICES_TOKEN])
    static getChartsDataByAccount(state: FinOpsAwsServicesStateModel) {
        return (account: string) => state.chartsDataByAccountByDate[account]
    }

    @Selector([FINOPS_AWS_SERVICES_TOKEN])
    static getChartsDataByAccountAndDate(state: FinOpsAwsServicesStateModel) {
        return (account: string, date: string) => state.chartsDataByAccountByDate[account][date]
    }

    @Selector([FINOPS_AWS_SERVICES_TOKEN])
    static getChartsDataDisplayed(state: FinOpsAwsServicesStateModel) {
        return state.chartsDataDisplayed
    }

    @Selector([FINOPS_AWS_SERVICES_TOKEN])
    static getFilteredHistoricalChartData(state: FinOpsAwsServicesStateModel) {
        return state.chartsDataDisplayed.filteredHistoricalChartData
    }

    @Selector([FINOPS_AWS_SERVICES_TOKEN])
    static getFilteredCompareChartData(state: FinOpsAwsServicesStateModel) {
        return state.chartsDataDisplayed.filteredCompareChartData
    }

    @Selector([FINOPS_AWS_SERVICES_TOKEN])
    static getHistoricalDataByAccountByGranularityByDate(state: FinOpsAwsServicesStateModel) {
        return (account:string, granularity: string, date: string) => state.historicalDataByAccountByGranularityByDate[account][granularity][date]
    }


    @Action(LoadFinOpsAwsServicesHistoricalData)
    loadFinOpsAwsServicesHistoricalData(ctx: StateContext<FinOpsAwsServicesStateModel>) {
        const finopsConfig = this.store.selectSnapshot(PreferencesFinopsState.getConfig)
        const awsConfig: AwsConfig = this.store.selectSnapshot(PreferencesProvidersState.getAwsConfig)
        const historicalDates: HistoricalDate[] = this.dateService.getFinopsHistoricalDates(finopsConfig)
        const selectedDateTile = this.store.selectSnapshot(FinOpsContextState.getSelectedDate)
        let wrapRequestList = []
        let accounts: string[] = []
        awsConfig.accounts.forEach(account => {
            accounts.push(account.account_id)
        });

        for (let account of accounts) {
            for (let historicalDate of historicalDates) {
                const FINOPS_ACCOUNT_DATE_KEY = this.KEY 
                + "-account-"
                + account
                + "-granularity-"
                + finopsConfig.granularity
                + "-date-"
                + historicalDate.date;

                if (!this.metaService.isStale(FINOPS_ACCOUNT_DATE_KEY)) {
                    if(isDevMode()) {
                        console.warn("Cache for KEY " + FINOPS_ACCOUNT_DATE_KEY + " still valid, no request executed.")
                    }
                    continue
                }
                wrapRequestList.push(
                    MetaRequestHelper.wrapRequest(
                        this.store,
                        FINOPS_ACCOUNT_DATE_KEY,
                        this.finOpsAwsServicesService.loadFinOpsAwsServicesHistoricalData(finopsConfig.granularity, account, historicalDate.date),
                        resources => {
                            ctx.setState(state => {
                                const h = { ...state.historicalDataByAccountByGranularityByDate };
                                h[account] = { ...(h[account] || {}) };
                                h[account][finopsConfig.granularity] = {
                                    ...(h[account][finopsConfig.granularity] || {}),
                                    [historicalDate.date]: resources.measures
                                };
                                return {
                                    ...state,
                                    historicalDataByAccountByGranularityByDate: h
                                };
                            });
                        }
                    )
                )
            }
        }

        return forkJoin(wrapRequestList).pipe(
            finalize( () => {
                    ctx.dispatch(
                        new LoadDataTableAllAccountsFinOpsAwsServices()
                    )
                }
            )
        )
    }

    @Action(LoadDataTableAllAccountsFinOpsAwsServices)
    loadDataTableAllAccountsFinOpsAwsServices(ctx: StateContext<FinOpsAwsServicesStateModel>) {
            const finopsConfig = this.store.selectSnapshot(PreferencesFinopsState.getConfig)
            const awsConfig: AwsConfig = this.store.selectSnapshot(PreferencesProvidersState.getAwsConfig)
            const granularity = finopsConfig.granularity as DateGranularityEnum
            const historicalDatesSelected = this.dateService.getFinopsHistoricalDates(finopsConfig).map(item => item.date)
            const filters = this.store.selectSnapshot(PreferencesFiltersState.getFiltersByType)[ItemTypeEnum[ItemTypeEnum.FINOPS_AWS_SERVICES]]
            const selectedDateTile = this.store.selectSnapshot(FinOpsContextState.getSelectedDate)
    
            // If cannot find selected date in historical dates, select the last one and load data for it
            if (selectedDateTile === undefined || !historicalDatesSelected.includes(selectedDateTile)) {
                ctx.dispatch([
                    new SelectFinOpsDate(historicalDatesSelected[historicalDatesSelected.length - 1]),
                    new LoadDataTableAllAccountsFinOpsAwsServices()
                ])
                return
            }

            if (isDevMode()) {
                console.warn("Loading all accounts data for granularity " + granularity + " and date " + selectedDateTile)
            }

            let total: Record<string, DataForDatatableCompute> = {}
            let grandTotal: DataForDatatableCompute = {
                filteredCounters: {},
                measuresDatatable: {},
                previousTotalCounters: {}
            }

            // // Iterate over all account
            for(let account of awsConfig.accounts) {
                const historicalDataByGranularity = ctx.getState().historicalDataByAccountByGranularityByDate[account.account_id][granularity]
                const dataForDatatable = FinOpsAwsServicesHelper.computeDatatable(historicalDatesSelected, historicalDataByGranularity, filters)
                total = {
                    ...total,
                    [account.account_id]: dataForDatatable
                }
            }

            grandTotal = FinOpsAwsServicesHelper.computeDatatableGrandTotal(total)

            ctx.patchState(
                {
                    datatable: grandTotal.measuresDatatable[selectedDateTile],
                    filteredCounters: grandTotal.filteredCounters
                }
            )
    }

    @Action(LoadFinOpsAwsServicesChartsData)
    loadFinOpsAwsServicesChartsData(ctx: StateContext<FinOpsAwsServicesStateModel>) {
        const chartsConfig = this.store.selectSnapshot(PreferencesChartsState.getConfig)
        const awsConfig: AwsConfig = this.store.selectSnapshot(PreferencesProvidersState.getAwsConfig)
        const historicalDates: HistoricalDate[] = this.dateService.getChartsHistoricalDates(chartsConfig)
        let wrapRequestList = []
        let accounts: string[] = []

        awsConfig.accounts.forEach(account => {
            accounts.push(account.account_id)
        });

        for(let _account of accounts) {
            for (let historicalDate of historicalDates) {
                const FINOPS_SERVICE_CHARTS_DATE_KEY = this.KEY
                + '-charts-'
                + 'account-' + _account
                + '-date-' + historicalDate.date
    
                if(!this.metaService.isStale(FINOPS_SERVICE_CHARTS_DATE_KEY)){
                    if(isDevMode()) {
                        console.warn("Cache for KEY " + FINOPS_SERVICE_CHARTS_DATE_KEY + " still valid, no request executed.")
                    }
                    continue
                }
    
                wrapRequestList.push(
                    MetaRequestHelper.wrapRequest(
                        this.store,
                        FINOPS_SERVICE_CHARTS_DATE_KEY,
                        this.finOpsAwsServicesService.loadFinOpsAwsServicesMeasures(historicalDate.date, _account),
                        (chart_resources) => {
                            ctx.setState(state => {
                                const shallowCloneAllChartsData = {...state.chartsDataByAccountByDate};
                                shallowCloneAllChartsData[_account] = { ...(shallowCloneAllChartsData[_account] || {})}
                                shallowCloneAllChartsData[_account][historicalDate.date] = chart_resources.measures
                                return {
                                    ...state,
                                    chartsDataByAccountByDate: shallowCloneAllChartsData
                                }
                            })
                        }
                    )
                )
            }
        }

        return forkJoin(wrapRequestList).pipe(
            finalize(() => {
                ctx.dispatch(new LoadFinOpsAwsServicesAllAccountsChartsFilteredData())
            })
        )
    }

    @Action(LoadFinOpsAwsServicesAllAccountsChartsFilteredData)
    loadFinOpsAwsServicesAllAccountsChartsFilteredData(ctx: StateContext<FinOpsAwsServicesStateModel>) {
        const chartsConfig = this.store.selectSnapshot(PreferencesChartsState.getConfig)
        const awsConfig: AwsConfig = this.store.selectSnapshot(PreferencesProvidersState.getAwsConfig)
        
        // All chartsData for all accounts and for all retrieved dates.
        const bulkChartsData = ctx.getState().chartsDataByAccountByDate

        // Retrieve granularity 
        const granularity = chartsConfig.granularity

        // Retrieve filters to adjust costs depending on selected filters
        const filters = this.store.selectSnapshot(PreferencesFiltersState.getFiltersByType)[ItemTypeEnum[ItemTypeEnum.FINOPS_AWS_SERVICES_CHARTS]]

        // Get all date to retrieve data from
        const historicalDatesSelected = this.dateService.getChartsHistoricalDates(chartsConfig).map(item => item.date)

        // Get date for compare Chart
        const computeChartDataLastDate = historicalDatesSelected[historicalDatesSelected.length -1]

        const aggregatedCompareData = FinOpsAwsServicesHelper.computeAggregatedCompareChartData(
            awsConfig,
            bulkChartsData,
            computeChartDataLastDate,
            granularity,
            filters,
            this.floorPipe,
            this.numberFormatPipe,
            this.dateService
        );

        const aggregatedHistoricalData = FinOpsAwsServicesHelper.computeAggregatedHistoricalChartData(
            awsConfig,
            historicalDatesSelected,
            bulkChartsData,
            filters,
            granularity,
            this.floorPipe,
            this.translate,
            this.dateService
        );

        ctx.patchState({
            chartsDataDisplayed: {
                filteredCompareChartData: aggregatedCompareData,
                filteredHistoricalChartData: aggregatedHistoricalData,
            },
        })
    }
}
